import { cn } from '@/lib/cn';

/**
 * Card.
 *
 * Borderless: a white surface separated from the mint canvas by a soft shadow alone.
 * The border it used to draw was doing the same job twice once the canvas gained its
 * tint, and a hairline plus a shadow is what makes an interface look like a form
 * rather than a product.
 *
 * Still NO lift on hover. A dashboard of rising cards reads as a consumer page, and it
 * makes genuinely elevated surfaces (menus, modals) indistinguishable from static ones.
 *
 * `interactive` is for cards that are themselves a link or button — those get a
 * background and shadow response, still without moving. See docs/DESIGN_SYSTEM.md §4.3.
 */
export function Card({
  className,
  interactive = false,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { interactive?: boolean }) {
  return (
    <div
      className={cn(
        'rounded-lg bg-surface shadow-sm',
        interactive &&
          'cursor-pointer transition-[background-color,box-shadow] duration-150 hover:bg-subtle hover:shadow-md',
        className,
      )}
      {...props}
    />
  );
}

export function CardHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('flex flex-col gap-1 p-5', className)} {...props} />;
}

export function CardTitle({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h3 className={cn('text-h3 font-semibold', className)} {...props}>
      {children}
    </h3>
  );
}

export function CardDescription({
  className,
  ...props
}: React.HTMLAttributes<HTMLParagraphElement>) {
  return <p className={cn('text-caption text-fg-muted', className)} {...props} />;
}

export function CardContent({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('p-5 pt-0', className)} {...props} />;
}

export function CardFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('flex items-center gap-2 border-t border-line bg-subtle px-5 py-3', className)}
      {...props}
    />
  );
}
